type InterruptLike = {
  value?: unknown;
};

export type HumanInTheLoopDecisionType = "approve" | "edit" | "reject";

export type HumanInTheLoopDecision =
  | { type: "approve" }
  | {
      type: "edit";
      edited_action: HumanInTheLoopActionRequest;
    }
  | { type: "reject"; message?: string };

export type HumanInTheLoopResponse = {
  decisions: HumanInTheLoopDecision[];
};

export type HumanInTheLoopActionRequest = {
  name: string;
  args: Record<string, unknown>;
  description?: string;
};

export type HumanInTheLoopReviewConfig = {
  action_name: string;
  allowed_decisions: HumanInTheLoopDecisionType[];
};

export type HumanInTheLoopRequest = {
  action_requests: HumanInTheLoopActionRequest[];
  review_configs: HumanInTheLoopReviewConfig[];
};

export const DEFAULT_HITL_REJECT_MESSAGE = "Rejected in Formly.";

const HUMAN_MESSAGE_REJECT_REASON =
  "The tool call was rejected because the user sent a new human message instead of approving it. Treat the user message below as the user's latest instruction.";
const HUMAN_MESSAGE_REJECT_USER_MESSAGE_LABEL = "User message:";
const HUMAN_MESSAGE_REJECT_USER_MESSAGE_SEPARATOR = `\n\n${HUMAN_MESSAGE_REJECT_USER_MESSAGE_LABEL}\n`;

export const buildHumanMessageRejectDecisionMessage = (userMessage: string) =>
  `${HUMAN_MESSAGE_REJECT_REASON}${HUMAN_MESSAGE_REJECT_USER_MESSAGE_SEPARATOR}${userMessage}`;

export const getUserMessageFromHumanMessageRejectDecisionMessage = (
  decisionMessage: string
) => {
  const marker = `${HUMAN_MESSAGE_REJECT_REASON}${HUMAN_MESSAGE_REJECT_USER_MESSAGE_SEPARATOR}`;
  const markerStart = decisionMessage.indexOf(marker);
  if (markerStart === -1) return null;

  const userMessage = decisionMessage.slice(markerStart + marker.length).trim();
  return userMessage.length > 0 ? userMessage : null;
};

export const isHumanInTheLoopRejection = (
  result: { status?: string; content?: unknown } | undefined
) => {
  if (result?.status !== "error" || typeof result.content !== "string")
    return false;
  const content = result.content.trim();
  return (
    content === DEFAULT_HITL_REJECT_MESSAGE ||
    content.startsWith("User rejected the tool call for ") ||
    getUserMessageFromHumanMessageRejectDecisionMessage(content) !== null
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getInterruptValue = (interrupt: InterruptLike | unknown) => {
  if (isRecord(interrupt) && "value" in interrupt) {
    return interrupt.value;
  }

  return interrupt;
};

export const getHumanInTheLoopRequest = (
  interrupt: InterruptLike | unknown
): HumanInTheLoopRequest | null => {
  const value = getInterruptValue(interrupt);
  if (!isRecord(value)) {
    return null;
  }

  const actionRequests = value.action_requests;
  const reviewConfigs = value.review_configs;
  if (!Array.isArray(actionRequests) || !Array.isArray(reviewConfigs)) {
    return null;
  }

  const parsedActions = actionRequests
    .map((action): HumanInTheLoopActionRequest | null => {
      if (!isRecord(action) || typeof action.name !== "string") {
        return null;
      }

      return {
        name: action.name,
        args: isRecord(action.args) ? action.args : {},
        description:
          typeof action.description === "string"
            ? action.description
            : undefined,
      };
    })
    .filter((action): action is HumanInTheLoopActionRequest => action !== null);

  const parsedConfigs = reviewConfigs
    .map((config): HumanInTheLoopReviewConfig | null => {
      if (
        !isRecord(config) ||
        typeof config.action_name !== "string" ||
        !Array.isArray(config.allowed_decisions)
      ) {
        return null;
      }

      const allowedDecisions = config.allowed_decisions.filter(
        (decision): decision is HumanInTheLoopDecisionType =>
          decision === "approve" || decision === "edit" || decision === "reject"
      );

      return {
        action_name: config.action_name,
        allowed_decisions: allowedDecisions,
      };
    })
    .filter((config): config is HumanInTheLoopReviewConfig => config !== null);

  if (parsedActions.length === 0 || parsedConfigs.length === 0) {
    return null;
  }

  return {
    action_requests: parsedActions,
    review_configs: parsedConfigs,
  };
};

const DATA_KEY_EDIT_TOOL_NAMES = new Set(["edit_data_key", "write_data_key"]);

export const doesHumanInTheLoopActionMatchToolCall = (
  actionName: string,
  toolName: string
) => {
  if (actionName === toolName) {
    return true;
  }

  return (
    DATA_KEY_EDIT_TOOL_NAMES.has(actionName) &&
    DATA_KEY_EDIT_TOOL_NAMES.has(toolName)
  );
};
